import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, DatePicker, message, Spin, Button, Input, Typography, Row, Col, Tag } from 'antd';
import { CalendarOutlined, EnvironmentOutlined, DollarOutlined, ClockCircleOutlined, ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const { Text } = Typography;

// Imágenes de fondo por tipo de deporte (en public/). Overlay oscuro para legibilidad del texto.
const getRutaImagenDeporte = (archivo) => `${process.env.PUBLIC_URL || ''}/${archivo}`;

const IMAGENES_CANCHA_POR_DEPORTE = {
  Tenis: getRutaImagenDeporte('tenis.jpeg'),
  Pádel: getRutaImagenDeporte('padel.png'),
  Picketball: getRutaImagenDeporte('picketball.png'),
  Pickleball: getRutaImagenDeporte('picketball.png'),
  Futbol: getRutaImagenDeporte('futbol.jpg'),
};

const getImagenFondoDeporte = (tipoDeporteNombre) => {
  if (!tipoDeporteNombre || typeof tipoDeporteNombre !== 'string') return null;
  const nombre = tipoDeporteNombre.trim();
  if (!nombre) return null;
  return IMAGENES_CANCHA_POR_DEPORTE[nombre] || IMAGENES_CANCHA_POR_DEPORTE.Tenis;
};

const formatHora = (hora) => (hora ? String(hora).substring(0, 5) : '-');

const getDuracionTexto = (horaDesde, horaHasta) => {
  if (!horaDesde || !horaHasta) return '-';
  const [desdeH, desdeM] = String(horaDesde).substring(0, 5).split(':').map(Number);
  const [hastaH, hastaM] = String(horaHasta).substring(0, 5).split(':').map(Number);
  if ([desdeH, desdeM, hastaH, hastaM].some(Number.isNaN)) return '-';
  const minutos = (hastaH * 60 + hastaM) - (desdeH * 60 + desdeM);
  if (minutos <= 0) return '-';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas > 0 && resto > 0) return `${horas}h ${resto} min`;
  if (horas > 0) return `${horas}h`;
  return `${resto} min`;
};

const AlumnoNuevoTurnoCancha = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [fecha, setFecha] = useState(dayjs());
  const [turnosDisponibles, setTurnosDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [reservando, setReservando] = useState(false);
  const [canchaSeleccionada, setCanchaSeleccionada] = useState(null);

  useEffect(() => {
    if (searchParams.get('mp') === 'failure') {
      message.error('El pago no se completó. Podés intentar de nuevo o reservar sin pago online.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadDisponibilidad = async () => {
    if (!fecha) return;
    setLoading(true);
    setCanchaSeleccionada(null);
    setTurnoSeleccionado(null);
    try {
      const f = fecha.format('YYYY-MM-DD');
      const response = await apiClient.get(`/mi/turnos-cancha/disponibilidad?fecha=${f}`);
      if (response.ok) {
        const data = await response.json();
        setTurnosDisponibles(Array.isArray(data) ? data : []);
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al cargar disponibilidad');
        setTurnosDisponibles([]);
      }
    } catch (error) {
      console.error('Error al cargar disponibilidad:', error);
      message.error('Error al cargar disponibilidad');
      setTurnosDisponibles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDisponibilidad();
  }, [fecha]);

  // Canchas que tienen al menos un slot disponible (agrupado por cancha)
  const canchasConDisponibilidad = () => {
    const map = {};
    turnosDisponibles.forEach((t) => {
      const id = t.tcl_cancha_club;
      if (!map[id]) {
        map[id] = {
          ccl_id: id,
          cancha_nombre: t.cancha_nombre,
          cancha_techada: t.cancha_techada,
          tipo_deporte_nombre: t.tipo_deporte_nombre || null,
          cantidad: 0,
        };
      }
      map[id].cantidad += 1;
    });
    return Object.values(map).sort((a, b) => String(a.cancha_nombre || '').localeCompare(String(b.cancha_nombre || '')));
  };

  // Horarios disponibles para la cancha seleccionada (ordenados por hora)
  const horariosDeLaCancha = () => {
    if (!canchaSeleccionada) return [];
    return turnosDisponibles
      .filter((t) => Number(t.tcl_cancha_club) === Number(canchaSeleccionada.ccl_id))
      .sort((a, b) => String(a.tcl_hora_desde || '').localeCompare(String(b.tcl_hora_desde || '')));
  };

  const seleccionarHorario = (turno) => {
    setTurnoSeleccionado(turno);
    if (!observaciones) setObservaciones('');
  };

  const cancelarConfirmacion = () => {
    setTurnoSeleccionado(null);
  };

  const confirmarReserva = async () => {
    if (!turnoSeleccionado) return;
    setReservando(true);
    try {
      const body = turnoSeleccionado.tcl_id
        ? {
            turnoClub: turnoSeleccionado.tcl_id,
            fecha: fecha.format('YYYY-MM-DD'),
            observaciones: observaciones.trim() || undefined,
            pagarConMercadoPago: true,
          }
        : {
            canchaClub: turnoSeleccionado.tcl_cancha_club,
            fecha: fecha.format('YYYY-MM-DD'),
            horaDesde: turnoSeleccionado.tcl_hora_desde,
            horaHasta: turnoSeleccionado.tcl_hora_hasta,
            precio: turnoSeleccionado.tcl_precio,
            observaciones: observaciones.trim() || undefined,
            pagarConMercadoPago: true,
          };
      const response = await apiClient.post('/mi/turnos-cancha/reservas', body);
      const data = await response.json();
      if (response.ok) {
        if (data.init_point) {
          message.loading('Redirigiendo a Mercado Pago para abonar la seña...', 1);
          setTimeout(() => {
            window.location.href = data.init_point;
          }, 500);
          return;
        }
        if (data.error) {
          message.warning({ content: data.error, duration: 6 });
        } else {
        message.success('Turno de cancha reservado correctamente');
        }
        setTurnoSeleccionado(null);
        setCanchaSeleccionada(null);
        setObservaciones('');
        loadDisponibilidad();
      } else {
        message.error(data.message || 'Error al reservar');
      }
    } catch (error) {
      console.error('Error al reservar:', error);
      message.error('Error al reservar el turno');
    } finally {
      setReservando(false);
    }
  };

  const canchas = canchasConDisponibilidad();
  const horarios = horariosDeLaCancha();
  const esTechada = (t) => t.cancha_techada === true || t.cancha_techada === 'true' || t.cancha_techada === 1;
  const stepActual = !canchaSeleccionada ? 0 : turnoSeleccionado ? 2 : 1;
  const stepItems = ['Cancha', 'Horario', 'Confirmar'];
  const horarioSeleccionado = turnoSeleccionado
    ? `${formatHora(turnoSeleccionado.tcl_hora_desde)} - ${formatHora(turnoSeleccionado.tcl_hora_hasta)}`
    : '-';
  const precioSeleccionado = turnoSeleccionado?.tcl_precio != null
    ? `$${Number(turnoSeleccionado.tcl_precio).toLocaleString('es-AR')}`
    : '-';

  return (
    <MainLayout>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <Card
          title={
            <span>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Nuevo Turno
            </span>
          }
          styles={{ body: { padding: 20 } }}
        >
          <div style={{ marginBottom: 20 }}>
            <div style={{ width: '100%', maxWidth: 520, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {stepItems.map((label, idx) => {
                  const isActive = idx === stepActual;
                  const isDone = idx < stepActual;
                  const circleBg = isActive ? '#d8ff3e' : isDone ? '#5f7a1f' : '#3a3d42';
                  const circleColor = isActive ? '#111111' : isDone ? '#d8ff3e' : '#b7bcc6';
                  const lineColor = idx < stepActual ? 'rgba(216,255,62,0.45)' : 'rgba(255,255,255,0.22)';
                  return (
                    <React.Fragment key={label}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 500,
                          background: circleBg,
                          color: circleColor,
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </div>
                      {idx < stepItems.length - 1 && (
                        <div
                          style={{
                            flex: 1,
                            height: 2,
                            margin: '0 12px',
                            background: lineColor,
                            borderRadius: 999,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 12 }}>
                {stepItems.map((label, idx) => (
                  <Text
                    key={label}
                    style={{
                      textAlign: idx === 0 ? 'left' : idx === stepItems.length - 1 ? 'right' : 'center',
                      fontSize: 13,
                      color: idx <= stepActual ? '#e5e9f2' : '#8e949d',
                    }}
                  >
                    {label}
                  </Text>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Día</Text>
            <DatePicker
              value={fecha}
              onChange={(d) => setFecha(d || dayjs())}
              format="DD/MM/YYYY"
              style={{ width: '100%', maxWidth: 260 }}
              allowClear={false}
            />
          </div>

          <Spin spinning={loading} tip="Cargando disponibilidad...">
            {!loading && turnosDisponibles.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.45)' }}>
                No hay horarios configurados para este día o todos están ocupados. Elegí otra fecha.
              </div>
            )}

            {!loading && turnosDisponibles.length > 0 && stepActual === 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
                  Elegí una cancha
                </Text>
                <Row gutter={[14, 14]}>
                  {canchas.map((c) => {
                    const imagenFondo = getImagenFondoDeporte(c.tipo_deporte_nombre);
                    const tieneFondo = !!imagenFondo;
                    return (
                      <Col xs={24} sm={12} lg={8} key={c.ccl_id}>
                        <Card
                          hoverable
                          onClick={() => {
                            setCanchaSeleccionada(c);
                            setTurnoSeleccionado(null);
                          }}
                          style={{
                            cursor: 'pointer',
                            borderColor: 'rgba(255,255,255,0.1)',
                            background: tieneFondo
                              ? `linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.72) 100%), url(${imagenFondo}) center/cover no-repeat`
                              : 'rgba(255,255,255,0.02)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                              <Text strong style={{ fontSize: 22, lineHeight: 1.2, color: '#fff' }}>
                                {c.cancha_nombre || 'Cancha'}
                              </Text>
                              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {c.tipo_deporte_nombre && <Tag color="blue">{c.tipo_deporte_nombre}</Tag>}
                                {esTechada(c) && <Tag color="lime">Techada</Tag>}
                              </div>
                              <Text style={{ display: 'block', marginTop: 8, color: '#cde864', fontWeight: 600 }}>
                                <ClockCircleOutlined style={{ marginRight: 6 }} />
                                {c.cantidad} horarios disponibles
                              </Text>
                            </div>
                            <ArrowLeftOutlined rotate={180} style={{ color: 'rgba(255,255,255,0.55)', fontSize: 18, marginTop: 4 }} />
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}

            {!loading && turnosDisponibles.length > 0 && stepActual >= 1 && canchaSeleccionada && (
              <>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => {
                    setCanchaSeleccionada(null);
                    setTurnoSeleccionado(null);
                  }}
                  style={{ marginBottom: 12, padding: 0 }}
                >
                  Volver a canchas
                </Button>

                {stepActual === 1 && (
                  <Card size="small" style={{ marginBottom: 14, background: 'rgba(255,255,255,0.02)' }}>
                    <Text strong style={{ fontSize: 22, lineHeight: 1.2 }}>
                      {canchaSeleccionada.cancha_nombre}
                    </Text>
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {canchaSeleccionada.tipo_deporte_nombre && <Tag color="blue">{canchaSeleccionada.tipo_deporte_nombre}</Tag>}
                      {esTechada(canchaSeleccionada) && <Tag color="lime">Techada</Tag>}
                    </div>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      Horarios disponibles - {fecha.format('DD/MM/YYYY')}
                    </Text>
                  </Card>
                )}

                {stepActual === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {horarios.map((t) => {
                      const slotKey = t.tcl_id ?? `${t.tcl_cancha_club}-${t.tcl_hora_desde}-${t.tcl_hora_hasta}`;
                      return (
                        <div
                          key={slotKey}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '14px 16px',
                            borderRadius: 12,
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(255,255,255,0.02)',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div>
                            <Text strong style={{ display: 'block', fontSize: 30, lineHeight: 1.05 }}>
                              {formatHora(t.tcl_hora_desde)} - {formatHora(t.tcl_hora_hasta)}
                            </Text>
                            <Text type="secondary">
                              <ClockCircleOutlined style={{ marginRight: 6 }} />
                              {getDuracionTexto(t.tcl_hora_desde, t.tcl_hora_hasta)}
                            </Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <Text strong style={{ fontSize: 24 }}>
                              ${Number(t.tcl_precio || 0).toLocaleString('es-AR')}
                            </Text>
                            <Button type="primary" size="large" onClick={() => seleccionarHorario(t)}>
                              Reservar
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {stepActual === 2 && turnoSeleccionado && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <Text strong style={{ fontSize: 22, lineHeight: 1.2 }}>
                          {canchaSeleccionada?.cancha_nombre || turnoSeleccionado.cancha_nombre}
                        </Text>
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {(canchaSeleccionada?.tipo_deporte_nombre || turnoSeleccionado.tipo_deporte_nombre) && (
                            <Tag color="blue">{canchaSeleccionada?.tipo_deporte_nombre || turnoSeleccionado.tipo_deporte_nombre}</Tag>
                          )}
                          {esTechada(canchaSeleccionada || turnoSeleccionado) && <Tag color="lime">Techada</Tag>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <Text type="secondary">Fecha</Text>
                        <Text strong>{fecha.format('dddd DD/MM/YYYY')}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <Text type="secondary">Horario</Text>
                        <Text strong style={{ color: '#d8ff3e' }}>{horarioSeleccionado}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <Text type="secondary">Duración</Text>
                        <Text strong>{getDuracionTexto(turnoSeleccionado.tcl_hora_desde, turnoSeleccionado.tcl_hora_hasta)}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 6px' }}>
                        <Text type="secondary">Total</Text>
                        <Text strong style={{ fontSize: 28 }}>{precioSeleccionado}</Text>
                      </div>
                    </Card>

                    <div>
                      <Text strong>Observaciones (opcional)</Text>
                      <Input.TextArea
                        rows={3}
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        placeholder="Notas adicionales"
                        style={{ marginTop: 8 }}
                      />
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      icon={<CheckOutlined />}
                      loading={reservando}
                      onClick={confirmarReserva}
                      style={{ width: '100%', height: 50 }}
                    >
                      Confirmar reserva
                    </Button>
                    <Button type="text" onClick={cancelarConfirmacion}>
                      Cancelar
                    </Button>
                  </div>
                )}
              </>
            )}
          </Spin>
        </Card>
        </div>
    </MainLayout>
  );
};

export default AlumnoNuevoTurnoCancha;
